// The brochure renderer. Pure: give it an event doc (with resolved imagePageUrls
// if source==="images") and a paletteId, it renders the scrollable experience.
// Used both in the live editor preview and on the public invite page.

import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { planPages } from "./planPages.js";
import { PAGE_COMPONENTS, ScrollRootContext } from "./pages.jsx";
import { getPalette, paletteVars } from "../../themes/index.js";
import { getBackground } from "../../backgrounds/registry.js";
import { urlFor } from "../../firebase/storage.js";
import "./brochure.css";

// Lazy-loads the background component only when a backgroundId is set.
// Each unique backgroundId gets its own stable lazy() reference (memoised
// outside the render path), so React never re-mounts for palette/set changes.
const lazyCache = {};
function getLazyBg(meta) {
  if (!lazyCache[meta.id]) lazyCache[meta.id] = lazy(meta.load);
  return lazyCache[meta.id];
}

function LazyBackground({ backgroundId, backgroundSetId }) {
  const meta = getBackground(backgroundId);
  if (!meta) return null;
  const BgComp = getLazyBg(meta);
  const setId = backgroundSetId ?? meta.defaultSetId;
  return (
    <Suspense fallback={null}>
      <BgComp setId={setId} />
    </Suspense>
  );
}

function CustomBackground({ background }) {
  if (!background?.path) return null;
  const src = urlFor(background.path);
  return (
    <div className="custom-brochure-bg" aria-hidden="true">
      {background.type === "video" ? (
        <video src={src} muted loop playsInline autoPlay />
      ) : (
        <img src={src} alt="" />
      )}
      <div className="custom-brochure-bg-scrim" />
    </div>
  );
}

export default function Brochure({ event, style, focusKind }) {
  const pages = useMemo(() => planPages(event), [event]);
  const palette = getPalette(event?.theme?.paletteId);
  const rootRef = useRef(null);
  // Track the scroll container in state so the IntersectionObserver root is set
  // once the element actually exists (a ref alone won't re-trigger children).
  const [scrollRoot, setScrollRoot] = useState(null);
  const [activePage, setActivePage] = useState(0);

  useEffect(() => {
    if (!focusKind || !rootRef.current) return;
    const target = rootRef.current.querySelector(`[data-page-kind="${focusKind}"]`);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focusKind, pages.length]);

  function scrollToPage(index) {
    const target = rootRef.current?.querySelector(`[data-brochure-page="${index + 1}"]`);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  useEffect(() => {
    setActivePage(0);
  }, [pages.length, event?.slug]);

  useEffect(() => {
    if (!scrollRoot) return undefined;
    const pageEls = Array.from(scrollRoot.querySelectorAll("[data-brochure-page]"));
    if (!pageEls.length) return undefined;

    const ratios = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
        });
        let bestIndex = 0;
        let bestRatio = -1;
        pageEls.forEach((pageEl, index) => {
          const ratio = ratios.get(pageEl) ?? 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestIndex = index;
          }
        });
        setActivePage(bestIndex);
      },
      {
        root: scrollRoot,
        threshold: [0.2, 0.35, 0.5, 0.65, 0.8],
      }
    );

    pageEls.forEach((pageEl) => {
      ratios.set(pageEl, 0);
      observer.observe(pageEl);
    });

    return () => observer.disconnect();
  }, [scrollRoot, pages.length]);

  const { backgroundId, backgroundSetId, customBackground } = event?.theme ?? {};
  const hasBackground = Boolean(customBackground?.path || backgroundId);

  const brochure = (
    <div
      className="brochure"
      data-has-bg={hasBackground ? "1" : undefined}
      ref={(el) => {
        rootRef.current = el;
        setScrollRoot(el);
      }}
      style={{ ...paletteVars(palette), ...(hasBackground ? {} : style) }}
    >
      {pages.length > 1 && (
        <div
          className="brochure-position-indicator"
          aria-label={`Invite page navigation, currently page ${activePage + 1} of ${pages.length}`}
        >
          <span className="brochure-position-label">
            {String(activePage + 1).padStart(2, "0")} / {String(pages.length).padStart(2, "0")}
          </span>
          <div className="brochure-position-dots">
            {pages.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`brochure-position-dot${index === activePage ? " is-active" : ""}`}
                aria-label={`Go to page ${index + 1}`}
                aria-current={index === activePage ? "page" : undefined}
                onClick={() => scrollToPage(index)}
              />
            ))}
          </div>
        </div>
      )}
      <ScrollRootContext.Provider value={scrollRoot ? { current: scrollRoot } : null}>
        {pages.map((p, i) => {
          const Comp = PAGE_COMPONENTS[p.kind];
          if (!Comp) return null;
          return (
            <div key={i} data-page-kind={p.kind} data-brochure-page={i + 1}>
              <Comp {...p.props} />
            </div>
          );
        })}
      </ScrollRootContext.Provider>
    </div>
  );

  // When a dynamic background is active, wrap in a clipping shell so the
  // background sits as a sibling of the scroll container — not inside it.
  // Inside the scroll container it would only cover the first page.
  if (hasBackground) {
    return (
      <div style={{ position: "relative", overflow: "hidden", height: "100dvh", ...style }}>
        {customBackground?.path ? (
          <CustomBackground background={customBackground} />
        ) : (
          <LazyBackground backgroundId={backgroundId} backgroundSetId={backgroundSetId} />
        )}
        {brochure}
      </div>
    );
  }

  return brochure;
}
